<template>
  <VaSidebar
    v-model="writableVisible"
    :minimized="isMinimized"
    minimized-width="70px"
    class="h-full custom-bar"
    color="primary"
  >
    <!-- LOGO -->
    <div class="sidebar-logo" :class="{ 'sidebar-logo--mini': isMinimized }">
      <Transition name="fade" mode="out-in">
        <img
          v-if="!isMinimized"
          key="logo-full"
          src="/logo_azul.png"
          alt="SpendFit"
          class="sidebar-logo__image sidebar-logo__image--full"
          @click="$router.push({ name: 'dashboard' })"
        />
        <img
          v-else
          key="logo-mini"
          src="/logo_vertical_azul.png"
          alt="SpendFit"
          class="sidebar-logo__image sidebar-logo__image--mini"
        />
      </Transition>
    </div>
    <div v-if="!isMinimized" class="sidebar-workspace">
      <WorkspaceSwitcher />
    </div>
    <!-- MENU -->
    <VaAccordion v-model="value" class="sidebar-nav" multiple @click.capture="pinSidebarScroll">
      <VaCollapse v-for="(route, index) in visibleRoutes" :key="index" :body-color="'#00000022'">
        <!-- HEADER -->
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.1"
          >
            <VaSidebarItemContent class="py-4 pr-2 pl-4">
              <VaIcon v-if="route.meta.icon" :name="route.meta.icon" size="20px" :color="iconColor(route)" />
              <VaSidebarItemTitle
                v-if="!isMinimized"
                class="flex justify-between items-center leading-5 font-light"
                :color="textColor(route)"
              >
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" color="warning" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>

        <!-- BODY / HIJOS: solo visible si NO está minimizado -->
        <template v-if="!isMinimized" #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(childRoute.displayName)}`"
              hover-opacity="0.1"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold" :color="textColor(childRoute)">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import navigationRoutes, { type INavigationRoute } from '../top-tabs-navegation/NavigationRoutesDesktop'
import { visibleNavigationRoutes } from '../top-tabs-navegation/visibleNavigation'
import { useUserStore } from '../../stores/user'
import WorkspaceSwitcher from './components/WorkspaceSwitcher.vue'

export default defineComponent({
  name: 'AppSidebar',
  components: {
    WorkspaceSwitcher,
  },
  props: {
    visible: { type: Boolean, default: true },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'update:minimized'],
  setup(props, { emit }) {
    const route = useRoute()
    const { t } = useI18n()
    const { getColor, colorToRgba } = useColors()
    const value = ref<boolean[]>([])
    const userStore = useUserStore()
    const visibleRoutes = computed(() =>
      visibleNavigationRoutes({
        personal: userStore.isPersonalWorkspace,
        business: userStore.isBusinessWorkspace,
        billingEnabled: userStore.billingEnabled,
      }),
    )

    // --- Sidebar visible / collapsed ---
    const writableVisible = computed({
      get: () => props.visible,
      set: (v) => emit('update:visible', v),
    })
    const isMinimized = computed({
      get: () => props.minimized,
      set: (v) => emit('update:minimized', v),
    })
    // --- Rutas activas ---
    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name
    const routeHasActiveChild = (section: INavigationRoute) =>
      section.children?.some((c) => route.name === c.name) ?? route.name === section.name
    const setActiveExpand = () => (value.value = visibleRoutes.value.map(routeHasActiveChild))
    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // --- Colores y utilidades ---
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))
    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'warning' : 'white')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'warning' : 'white')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const pinSidebarScroll = (event: Event) => {
      const nav = (event.target as HTMLElement | null)?.closest?.('.sidebar-nav') as HTMLElement | null
      if (!nav) return
      const top = nav.scrollTop
      const lock = () => {
        if (nav.scrollTop !== top) nav.scrollTop = top
      }
      nav.addEventListener('scroll', lock)
      const started = performance.now()
      const tick = () => {
        lock()
        if (performance.now() - started < 400) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
      window.setTimeout(() => nav.removeEventListener('scroll', lock), 400)
    }

    return {
      writableVisible,
      isMinimized,
      //expandOnHover,
      //collapseOnLeave,
      value,
      navigationRoutes,
      visibleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      pinSidebarScroll,
      activeColor,
    }
  },
})
</script>
<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.custom-bar {
  background: linear-gradient(180deg, var(--va-primary) 0%, #0a0a0a 100%);
}

.sidebar-logo {
  position: relative;
  flex-shrink: 0;
  width: min(11.25rem, calc(100% - 1.5rem));
  margin: 0.55rem auto 1rem;
  aspect-ratio: 1281 / 406;
  overflow: hidden;
}

.sidebar-logo__image {
  position: absolute;
  display: block;
  max-width: none;
}

.sidebar-logo__image--full {
  width: 119.91%;
  height: 252.22%;
  left: -10.85%;
  top: -68.23%;
  cursor: pointer;
}

.sidebar-logo--mini {
  width: calc(100% - 1rem);
  margin: 0.4rem auto 0.15rem;
  aspect-ratio: 720 / 678;
}

.sidebar-logo__image--mini {
  width: 142.22%;
  height: 226.55%;
  left: -22.78%;
  top: -58.26%;
}

.sidebar-workspace {
  flex-shrink: 0;
  width: 100%;
  padding: 0 12px 16px;
}

.sidebar-workspace :deep(.workspace-select) {
  width: 100%;
  max-width: none;
}

.custom-bar.va-sidebar--animated,
.custom-bar.va-sidebar--animated .va-sidebar__menu {
  transition: width 0.2s ease, min-width 0.2s ease, left 0.2s ease;
}

.custom-bar .va-sidebar__menu {
  justify-content: flex-start;
  overflow: hidden;
}

.custom-bar .sidebar-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overflow-anchor: none;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
}

.custom-bar .sidebar-nav::-webkit-scrollbar {
  width: 3px;
}

.custom-bar .sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.custom-bar .sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.28);
  border-radius: 999px;
}

.custom-bar .va-collapse {
  transition: none;
}

.custom-bar .va-collapse__header,
.custom-bar .va-sidebar-item {
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.custom-bar .va-collapse__body-wrapper {
  overflow: hidden;
  overflow-anchor: none;
  transform-origin: top center;
}
</style>
